import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AttachFile as AttachFileIcon, Send as SendIcon } from '@mui/icons-material';
import disputeAdminService from '../../services/disputeAdminService';

const CreateDisputeForm = ({ reservationId, onSuccess, onCancel }) => {
  const { t } = useTranslation('disputes');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [autoHoldDeposit, setAutoHoldDeposit] = useState(true);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !description || !category) {
      setError(t('validation.requiredFields'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('reservation_id', reservationId);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('category', category);
      formData.append('auto_hold_deposit', autoHoldDeposit);

      files.forEach((file) => {
        formData.append('attachments', file);
      });
      
      await disputeAdminService.create(formData);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error creating dispute:', err);
      setError(err.response?.data?.error || t('form.error.generic', 'Une erreur est survenue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('create.title', 'Signaler un problème')}
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={3}>
        <TextField
          label={t('form.title', 'Titre du problème')}
          fullWidth
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <FormControl fullWidth required>
          <InputLabel>{t('form.category', 'Catégorie')}</InputLabel>
          <Select
            value={category}
            label={t('form.category', 'Catégorie')}
            onChange={(e) => setCategory(e.target.value)}
          >
            <MenuItem value="damage">{t('categories.damage', 'Dommages matériels')}</MenuItem>
            <MenuItem value="cleanliness">{t('categories.cleanliness', 'Propreté')}</MenuItem>
            <MenuItem value="late_return">{t('categories.late_return', 'Retard de retour')}</MenuItem>
            <MenuItem value="item_mismatch">{t('categories.item_mismatch', 'Non-conformité')}</MenuItem>
            <MenuItem value="other">{t('categories.other', 'Autre')}</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label={t('form.description', 'Description détaillée')}
          fullWidth
          required
          multiline
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={autoHoldDeposit}
              onChange={(e) => setAutoHoldDeposit(e.target.checked)}
              color="primary"
            />
          }
          label={t('form.autoHold', 'Bloquer automatiquement la caution (si applicable)')}
        />

        <Button
          variant="outlined"
          component="label"
          startIcon={<AttachFileIcon />}
        >
          {t('form.addEvidence', 'Ajouter des preuves (photos/vidéos)')}
          <input
            type="file"
            hidden
            multiple
            onChange={handleFileChange}
          />
        </Button>
        {files.length > 0 && (
          <Typography variant="caption">
            {files.length} {t('common.filesSelected', 'fichiers sélectionnés')}
          </Typography>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {t('common.submit', 'Signaler le litige')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CreateDisputeForm;